import { selectRenderVariant } from "@desk/core";
import type {
  RendererAdapter, RenderFrameRequest, RenderFrameResult,
  ExportRequest, ExportResult, PreviewSession,
} from "@desk/core";
import type { EditGraph, EditOp, ResolvedEdit, SourcePack } from "@desk/core";
import {
  HybridPreviewSession,
  resolveActiveStemAssetId,
  type AudioElementFactory,
  type VideoElementFactory,
} from "./preview-session.js";
import { buildHybridTimeline, hybridEditTickToVideoTimeSec } from "./lib/hybrid-timeline.js";
import { seekVideoWithGate } from "./lib/video-seek.js";
import { renderStatefulResultCard } from "../../shell/lib/result-card.js";
import { resolvePreviewCaption } from "../../shell/lib/caption-preview.js";
import { shouldShowReconstructionLabel } from "../../game/disclosure.js";
import {
  computeFrameDrawGeometry,
  drawVideoWithGeometry,
  geometryToCssVideoCrop,
  applyCssVideoCrop,
  type DrawImageGeometry,
} from "../../shell/lib/frame-geometry.js";
import {
  sampleLook,
  sampleCreativeRenderState,
  type CreativeLookState,
  type CreativeOverlayModel,
} from "../../shell/lib/creative-render-state.js";
import { composeCreativeOverlay } from "../../shell/lib/creative-overlay-compose.js";
import { preloadAllStickerArt } from "../../shell/lib/sticker-art-cache.js";
import { fxKindForLook, type FxKind } from "../../shell/lib/creative-fx.js";

// Fire-and-forget warm-up at module load — best-effort so images are already
// decoding by the time an export runs. `exportReel` still awaits its own
// `preloadAllStickerArt()` call (idempotent) as the load-bearing guarantee,
// since module-init timing isn't reliable enough on its own.
void preloadAllStickerArt();

/**
 * Compute the CSS filter string for a look grade, using the same lerp values
 * that `applyLook` writes to the live preview video's `style.filter`.
 * Returns an empty string when no look is active (treatmentId null).
 */
function lookStateToFilterString(look: CreativeLookState): string {
  if (!look.treatmentId) return "";
  const { contrast, saturation, warmth, vignette } = look;
  const hueDeviation = warmth - 1;
  const hue = hueDeviation * 18;
  const sepia = Math.max(0, hueDeviation) * 0.35;
  // Vignette contributes a global brightness darkening (same as applyLook).
  const bright = 1 - vignette / 4;
  return `contrast(${contrast}) saturate(${saturation}) hue-rotate(${hue}deg) sepia(${sepia}) brightness(${bright})`;
}

/**
 * Draw a STATIC approximation of the advanced editor FX onto the export canvas
 * (the result card is a single frame). Animated fx are represented by one frame:
 * - focus-blur: a real blur of the surround with a sharp radial center (subject).
 * - rain: drawn diagonal streaks.
 * - lightning: a bolt + faint white flash.
 * All draws are guarded so happy-dom (no real 2d) degrades gracefully.
 */
function drawExportFx(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  geom: DrawImageGeometry,
  look: CreativeLookState,
  width: number,
  height: number,
): void {
  const kind: FxKind | null = fxKindForLook(look.treatmentId);
  if (!kind) return;

  const cx = width * 0.5;
  const cy = height * 0.52;

  if (kind === "focus-blur") {
    try {
      // Blur the whole frame, then punch a sharp radial center back in.
      ctx.save();
      ctx.filter = "blur(6px)";
      drawVideoWithGeometry(ctx, video, geom);
      ctx.restore();

      ctx.save();
      ctx.beginPath();
      ctx.ellipse(cx, cy, width * 0.44, height * 0.4, 0, 0, Math.PI * 2);
      ctx.clip();
      ctx.filter = "none";
      drawVideoWithGeometry(ctx, video, geom);
      ctx.restore();

      // Gentle edge darkening for depth.
      const grad = ctx.createRadialGradient(cx, cy, height * 0.3, cx, cy, height * 0.62);
      grad.addColorStop(0, "rgba(0,0,0,0)");
      grad.addColorStop(1, "rgba(0,0,0,0.2)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);
    } catch {
      /* no-op: filter / ellipse / gradient unavailable in test env */
    }
    return;
  }

  if (kind === "rain") {
    try {
      ctx.save();
      ctx.strokeStyle = "rgba(220,234,255,0.34)";
      ctx.lineWidth = Math.max(1, width * 0.004);
      const dx = width * 0.05;
      const dy = height * 0.14;
      const step = width * 0.055;
      for (let x = -width * 0.2; x < width * 1.2; x += step) {
        // Pseudo-random-but-deterministic jitter per streak.
        const j = ((Math.sin(x * 12.9898) * 43758.5453) % 1 + 1) % 1;
        const sx = x + j * step;
        const sy = -height * 0.05 + j * height * 0.6;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx - dx, sy + dy);
        ctx.stroke();
      }
      ctx.restore();
    } catch {
      /* no-op */
    }
    return;
  }

  // lightning
  try {
    // Faint flash.
    const flash = ctx.createRadialGradient(
      width * 0.6,
      height * 0.2,
      0,
      width * 0.6,
      height * 0.2,
      height * 0.7,
    );
    flash.addColorStop(0, "rgba(255,255,255,0.4)");
    flash.addColorStop(0.5, "rgba(214,228,255,0.14)");
    flash.addColorStop(1, "rgba(255,255,255,0)");
    ctx.save();
    ctx.fillStyle = flash;
    ctx.fillRect(0, 0, width, height);

    // Simple bolt.
    const bx = width * 0.6;
    const by = height * 0.04;
    const s = height * 0.62;
    ctx.beginPath();
    ctx.moveTo(bx + s * 0.09, by);
    ctx.lineTo(bx - s * 0.14, by + s * 0.45);
    ctx.lineTo(bx, by + s * 0.45);
    ctx.lineTo(bx - s * 0.08, by + s);
    ctx.lineTo(bx + s * 0.22, by + s * 0.37);
    ctx.lineTo(bx + s * 0.04, by + s * 0.37);
    ctx.lineTo(bx + s * 0.2, by);
    ctx.closePath();
    ctx.fillStyle = "#fdfcff";
    ctx.fill();
    ctx.restore();
  } catch {
    /* no-op */
  }
}

export class HybridVideoRendererAdapter implements RendererAdapter {
  readonly id = "hybrid-video-h3";
  readonly label = "AI-generated video (MiniMax H3)";

  private videoFactory: VideoElementFactory = () => document.createElement("video");
  private audioFactory: AudioElementFactory = () => document.createElement("audio");
  private tickListener: ((tick: number) => void) | null = null;
  private session: HybridPreviewSession | null = null;
  /** Max time to wait for a freshly-built offscreen capture video to decode a frame.
   *  Editor→Publish/Compare captures hit the browser HTTP cache (the editor just
   *  played the asset), so this bounds cold loads without blocking the serial
   *  capture queue indefinitely — a miss falls back to the source-record poster. */
  private captureLoadTimeoutMs = 4000;

  /** Stores the latest resolved edit for per-tick visual updates. */
  private _resolved: ResolvedEdit | null = null;
  /** Stores the active source pack for look treatment lookup. */
  private _pack: SourcePack | null = null;
  /** Vignette radial-gradient overlay div appended inside the mount. */
  private _vignetteEl: HTMLDivElement | null = null;
  /** Test seam: override mount container dimensions (offsetWidth/offsetHeight = 0 in happy-dom). */
  private _testContainerW = 0;
  private _testContainerH = 0;
  /** Cached mount pixel size, refreshed only on real resize (see `containerSize`)
   *  so per-tick frame-crop never reads offsetWidth/offsetHeight (a layout reflow). */
  private _containerW = 0;
  private _containerH = 0;
  private _sizedMount: HTMLElement | null = null;
  private _resizeObs: ResizeObserver | null = null;
  /**
   * Transient LIVE frame override for interactive Focus panning. While the player
   * drags to pan, FrameTool pushes the in-progress crop here so the preview
   * reframes in real time WITHOUT committing an undo entry per pointer-move. It
   * applies only to its `beatId` (the beat under the playhead being panned) and
   * is cleared on pointer-up, when the final crop is committed as a single op.
   */
  private _liveFrameOverride: {
    beatId: string;
    op: Extract<EditOp, { kind: "frame" }>;
  } | null = null;

  /**
   * Test seam — inspectable after exportReel to verify look/vignette/overlay were applied.
   * Not part of the public API; underscore prefix signals internal-only use.
   */
  _lastExportLookFilter = "";
  _lastExportVignetteStrength = 0;
  _lastExportOverlayStickerCount = 0;

  supports(sourcePack: SourcePack): boolean {
    try {
      const variant = selectRenderVariant(sourcePack, "hybrid-video");
      const videoAssetId = variant.config?.videoAssetId;
      if (!videoAssetId) return false;
      return sourcePack.assets.some((a) => a.id === String(videoAssetId) && a.kind === "visual");
    } catch {
      return false;
    }
  }

  setVideoFactoryForTests(factory: VideoElementFactory | null): void {
    this.videoFactory = factory ?? (() => document.createElement("video"));
  }

  setAudioFactoryForTests(factory: AudioElementFactory | null): void {
    this.audioFactory = factory ?? (() => document.createElement("audio"));
  }

  /** Test seam: shorten the offscreen-capture video load timeout for determinism. */
  setCaptureLoadTimeoutMsForTests(ms: number | null): void {
    this.captureLoadTimeoutMs = ms ?? 4000;
  }

  setTickListener(listener: ((tick: number) => void) | null): void {
    this.tickListener = listener;
  }

  /** Test seam: provide explicit container dimensions so crop CSS is deterministic in happy-dom. */
  setContainerSizeForTests(w: number, h: number): void {
    this._testContainerW = w;
    this._testContainerH = h;
  }

  getPreviewSession(): HybridPreviewSession | null {
    return this.session;
  }

  /**
   * Resolve the active frame op (from resolved.frames) for the given edit tick.
   * Looks up the segment in the current session timeline, then reads resolved.frames.
   */
  private activeFrameOpAtTick(tick: number): Extract<EditOp, { kind: "frame" }> | null {
    if (!this._resolved || !this.session) return null;
    const timeline = this.session.currentTimeline;
    const clamped = Math.max(0, Math.min(tick, Math.max(0, timeline.durationTicks - 1)));
    const activeSeg =
      timeline.segments.find((s) => clamped >= s.editStartTick && clamped < s.editEndTick) ??
      timeline.segments[timeline.segments.length - 1] ??
      null;
    if (!activeSeg) return null;
    // Live pan override wins for the beat being dragged (uncommitted, real-time).
    if (this._liveFrameOverride && this._liveFrameOverride.beatId === activeSeg.beatId) {
      return this._liveFrameOverride.op;
    }
    return this._resolved.frames.get(activeSeg.beatId) ?? null;
  }

  /**
   * Push (or clear) a transient live crop for interactive Focus panning. Passing
   * a frame op reframes the preview immediately for that beat without touching
   * the edit graph; passing null clears it and re-applies the committed crop.
   */
  setLiveFrameOverride(override: Extract<EditOp, { kind: "frame" }> | null): void {
    this._liveFrameOverride = override ? { beatId: override.beatId, op: override } : null;
    this.applyFrameCrop(this.session?.currentTick ?? 0);
  }

  /**
   * Apply the resolved frame crop/zoom to the video element as CSS placement.
   * Uses the shell's computeFrameDrawGeometry + geometryToCssVideoCrop helpers.
   * The mount (reel-frame) is position:relative;overflow:hidden — video is absolutely
   * positioned inside it to implement the crop.
   */
  private applyFrameCrop(tick: number): void {
    const video = this.session?.video;
    const mount = this.session?.videoHost;
    if (!video || !mount) return;

    const size = this.containerSize(mount);
    const vw = video.videoWidth || this._testContainerW || 360;
    const vh = video.videoHeight || this._testContainerH || 640;
    const cw = this._testContainerW || size.w || 360;
    const ch = this._testContainerH || size.h || 640;

    const frameOp = this.activeFrameOpAtTick(tick);
    const geom = computeFrameDrawGeometry(frameOp, vw, vh, cw, ch);
    const cssCrop = geometryToCssVideoCrop(geom, vw, vh);
    applyCssVideoCrop(video, cssCrop);
  }

  /**
   * The mount's pixel size, CACHED. Reading offsetWidth/offsetHeight forces a
   * layout reflow, and `applyFrameCrop` runs every tick right after writing the
   * video crop CSS — so a per-tick read reflows the page on every scrub frame.
   * Instead we measure once and refresh only when the element actually resizes
   * (ResizeObserver, which fires rarely). The observer is disconnected on
   * dispose. Falls back to a one-shot measure where ResizeObserver is absent.
   */
  private containerSize(mount: HTMLElement): { w: number; h: number } {
    if (this._sizedMount !== mount) {
      this._sizedMount = mount;
      this._containerW = mount.offsetWidth;
      this._containerH = mount.offsetHeight;
      this._resizeObs?.disconnect();
      if (typeof ResizeObserver !== "undefined") {
        this._resizeObs = new ResizeObserver(() => {
          this._containerW = mount.offsetWidth;
          this._containerH = mount.offsetHeight;
        });
        this._resizeObs.observe(mount);
      } else {
        this._resizeObs = null;
      }
    }
    return { w: this._containerW, h: this._containerH };
  }

  /**
   * Apply the resolved look grade/vignette to the video element as CSS filter,
   * mirroring the intensity lerp used by the 3D path (lookFromTreatment in
   * creative-render-state.ts). Also updates the vignette overlay div.
   *
   * Formula: lerp(neutral, treatment, intensity) where neutral = 1 for contrast/sat/warmth, 0 for vignette.
   */
  private applyLook(resolved: ResolvedEdit): void {
    const video = this.session?.video;
    if (!video) return;

    const lookOp = resolved.look;
    if (!lookOp?.treatmentId || !this._pack) {
      video.style.filter = "";
      if (this._vignetteEl) this._vignetteEl.style.opacity = "0";
      return;
    }

    const treatment = (this._pack.lookTreatments ?? []).find(
      (t) => t.id === lookOp.treatmentId,
    );
    if (!treatment) {
      video.style.filter = "";
      if (this._vignetteEl) this._vignetteEl.style.opacity = "0";
      return;
    }

    // Mirror lookFromTreatment: lerp between neutral and full treatment, scaled by intensity.
    const intensity = treatment.intensitySupported
      ? Math.max(0, Math.min(1, (lookOp.intensityMilli ?? treatment.defaultIntensityMilli) / 1000))
      : 1.0;

    const contrast = 1 + (treatment.contrastMilli / 1000 - 1) * intensity;
    const saturation = 1 + (treatment.saturationMilli / 1000 - 1) * intensity;
    const warmthFactor = 1 + (treatment.warmthMilli / 1000 - 1) * intensity;
    const vignette = (treatment.vignetteMilli / 1000) * intensity;

    // Warmth → hue-rotate + sepia (matches lookPreviewStyle formula).
    const hueDeviation = warmthFactor - 1;
    const hue = hueDeviation * 18;
    const sepia = Math.max(0, hueDeviation) * 0.35;
    // Vignette → global brightness darkening (lightweight, CSS-only).
    const bright = 1 - vignette / 4;

    video.style.filter = `contrast(${contrast}) saturate(${saturation}) hue-rotate(${hue}deg) sepia(${sepia}) brightness(${bright})`;

    // Radial-gradient vignette overlay for a more cinematic effect.
    if (this._vignetteEl && vignette > 0.01) {
      const strength = Math.min(0.85, vignette * 1.2);
      this._vignetteEl.style.backgroundImage = `radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,${strength}) 100%)`;
      this._vignetteEl.dataset.vignetteStrength = strength.toFixed(4);
      this._vignetteEl.style.opacity = "1";
    } else if (this._vignetteEl) {
      this._vignetteEl.style.opacity = "0";
      this._vignetteEl.dataset.vignetteStrength = "0";
    }
  }

  async createPreview(pack: SourcePack, graph: EditGraph, resolved: ResolvedEdit, mount: HTMLElement): Promise<PreviewSession> {
    const variant = selectRenderVariant(pack, "hybrid-video");
    const videoAssetId = String(variant.config.videoAssetId);
    const asset = pack.assets.find((a) => a.id === videoAssetId);
    const video = this.videoFactory();
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.classList.add("hybrid-fg");
    if (asset) video.src = `/${asset.path}`;
    // Foreground must stay the FIRST <video> in DOM order (querySelectors + tests
    // read `mount.querySelector("video")` as the foreground). The blurred-fill copy
    // is appended after it and layered BEHIND via CSS z-index.
    mount.appendChild(video);

    // Blurred fill: a full-bleed cover copy of the same footage behind the
    // contained foreground, so letterbox/pillarbox bars show soft blurred footage
    // instead of empty space. Muted, decode-only; heavy CSS blur hides any drift.
    const blurVideo = this.videoFactory();
    blurVideo.muted = true;
    blurVideo.playsInline = true;
    blurVideo.preload = "auto";
    blurVideo.classList.add("hybrid-blur-fill");
    (blurVideo as HTMLVideoElement).setAttribute?.("aria-hidden", "true");
    if (asset) blurVideo.src = `/${asset.path}`;
    mount.appendChild(blurVideo);

    // Vignette overlay — absolutely positioned over the video inside the crop container.
    const vignetteEl = document.createElement("div") as HTMLDivElement;
    vignetteEl.className = "hybrid-overlay";
    vignetteEl.style.opacity = "0";
    vignetteEl.style.pointerEvents = "none";
    mount.appendChild(vignetteEl);
    this._vignetteEl = vignetteEl;

    // Create the audio element for the swappable stem; src set by the session constructor
    const audio = this.audioFactory();
    mount.appendChild(audio);

    this._pack = pack;
    this._resolved = resolved;

    void graph; // graph is part of the interface contract; not consumed by the session
    this.session = new HybridPreviewSession(
      video, audio, mount, pack, variant,
      (t) => {
        // Apply frame crop per-tick so Wide/Subject/Close reframes update in real-time.
        this.applyFrameCrop(t);
        this.tickListener?.(t);
      },
      () => {
        this.session = null;
        this._resizeObs?.disconnect();
        this._resizeObs = null;
        this._sizedMount = null;
      },
      resolved,
    );
    // Hand the blurred-fill copy to the session so it rides play/seek/pause and is
    // torn down on dispose (the "dispose removes the video element" contract).
    this.session.attachBlurFillVideo(blurVideo);

    // Apply initial visuals synchronously after session creation.
    this.applyLook(resolved);
    this.applyFrameCrop(0);

    return this.session;
  }

  updatePreviewGraph(graph: EditGraph, resolved: ResolvedEdit): void {
    this._resolved = resolved;
    this.session?.updateTimeline(graph, resolved);
    // Re-apply visuals immediately so look/crop changes take effect without a seek.
    this.applyLook(resolved);
    this.applyFrameCrop(this.session?.currentTick ?? 0);
  }

  disposePreview(): void {
    // Remove the vignette overlay (not owned by the session).
    this._vignetteEl?.remove();
    this._vignetteEl = null;
    this._resolved = null;
    this._pack = null;
    this.session?.dispose();
  }

  async renderAtTime(request: RenderFrameRequest): Promise<RenderFrameResult> {
    if (this.session) await this.session.seek(request.tick);
    const handle = this.session?.videoHost ?? document.createElement("div");
    return { handle, tick: request.tick };
  }

  /**
   * MF1 (self-contained capture): build an offscreen `<video>` for the REQUEST's
   * pack, appended to an offscreen host detached from the live editor preview.
   * Pack-keyed: the source is always resolved from `sourcePack`, never a cached
   * editor session — so a capture on Publish/Compare/Portfolio (where the editor
   * has unmounted) renders the requested pack, not whatever was last mounted.
   */
  private buildOffscreenCaptureVideo(
    sourcePack: SourcePack,
    variant: ReturnType<typeof selectRenderVariant>,
  ): { video: HTMLVideoElement; host: HTMLDivElement } | null {
    const videoAssetId = variant.config?.videoAssetId
      ? String(variant.config.videoAssetId)
      : "";
    if (!videoAssetId) return null;
    const asset = sourcePack.assets.find(
      (a) => a.id === videoAssetId && a.kind === "visual",
    );
    if (!asset) return null;

    const host = document.createElement("div");
    host.dataset.deskHybridCaptureHost = "1";
    host.style.cssText =
      "position:fixed;left:-10000px;top:0;width:360px;height:640px;opacity:0;pointer-events:none;";
    if (typeof document !== "undefined" && document.body) {
      document.body.appendChild(host);
    }

    const video = this.videoFactory();
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.src = `/${asset.path}`;
    host.appendChild(video);
    return { video, host };
  }

  /**
   * Wait until the offscreen capture video has a decodable frame. Resolves true
   * once `readyState >= HAVE_CURRENT_DATA`, on `loadeddata`/`canplay`, or (on the
   * bounded timeout) based on the current readyState. A timeout is never treated
   * as success unless a frame is actually available — mirrors the H4 gate ethos.
   */
  private waitForCaptureVideoReady(video: HTMLVideoElement): Promise<boolean> {
    const HAVE_CURRENT_DATA = 2;
    if ((video.readyState ?? 0) >= HAVE_CURRENT_DATA) return Promise.resolve(true);
    try {
      video.load?.();
    } catch {
      /* happy-dom / no-src: ignore */
    }
    return new Promise<boolean>((resolve) => {
      let done = false;
      const finish = (ok: boolean): void => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        video.removeEventListener("loadeddata", onLoad);
        video.removeEventListener("canplay", onLoad);
        video.removeEventListener("error", onError);
        resolve(ok);
      };
      const onLoad = (): void => finish(true);
      const onError = (): void => finish(false);
      const timer = setTimeout(
        () => finish((video.readyState ?? 0) >= HAVE_CURRENT_DATA),
        this.captureLoadTimeoutMs,
      );
      video.addEventListener("loadeddata", onLoad);
      video.addEventListener("canplay", onLoad);
      video.addEventListener("error", onError);
    });
  }

  /**
   * Pre-composite the video frame (with look grade filter + vignette) and the
   * creative overlay (stickers + transitions) into a single canvas.
   *
   * This mirrors the 3D path's `compositeFrameWithOverlay` pattern:
   * - Look filter: `ctx.filter` is set before `drawImage` so the grade is baked in.
   * - Vignette: a radial-gradient fill is drawn over the frame (matches `applyLook`).
   * - Overlay: `composeCreativeOverlay` draws stickers + transitions (caption is
   *   excluded — `renderStatefulResultCard` handles it in its caption pass).
   *
   * Returns `{ canvas, drawn }` where `drawn` is false when the real `drawImage`
   * threw (e.g. video not decoded, CORS block after the probe passed). Callers
   * MUST check `drawn` and return `encode-failed` when it is false, rather than
   * emitting a background-only card as `ok:true`.
   */
  private buildExportCanvas(
    video: HTMLVideoElement,
    geom: DrawImageGeometry,
    look: CreativeLookState,
    overlay: CreativeOverlayModel,
    width: number,
    height: number,
  ): { canvas: HTMLCanvasElement; drawn: boolean } {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return { canvas, drawn: false };
    }

    // Apply look filter before drawing the frame so the grade is baked into the pixels.
    const filterStr = lookStateToFilterString(look);
    if (filterStr) {
      try { ctx.filter = filterStr; } catch { /* no-op in environments without filter */ }
    }
    // Expose for test inspection.
    this._lastExportLookFilter = filterStr;

    let drawn = false;
    try {
      drawVideoWithGeometry(ctx, video, geom);
      drawn = true;
    } catch {
      // Real frame draw failed (decode race, CORS block, etc.).
      // Caller propagates as encode-failed — do NOT return a background-only canvas.
    }

    // Reset filter before drawing overlay layers so they are not colour-graded.
    try { ctx.filter = "none"; } catch { /* no-op */ }

    // Vignette radial-gradient overlay — matches the applyLook live preview formula.
    const vignette = look.vignette;
    if (vignette > 0.01) {
      const strength = Math.min(0.85, vignette * 1.2);
      this._lastExportVignetteStrength = strength;
      const gx = width / 2;
      const gy = height / 2;
      const r = Math.sqrt(gx * gx + gy * gy);
      try {
        const grad = ctx.createRadialGradient(gx, gy, 0, gx, gy, r);
        grad.addColorStop(0.5, "rgba(0,0,0,0)");
        grad.addColorStop(1, `rgba(0,0,0,${strength.toFixed(4)})`);
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);
      } catch {
        // happy-dom: gradient API may be stubbed; skip vignette gracefully.
      }
    } else {
      this._lastExportVignetteStrength = 0;
    }

    // Advanced FX static approximation (rain / lightning / radial focus blur).
    // Drawn under the stickers so on-subject props stay legible on top.
    drawExportFx(ctx, video, geom, look, width, height);

    // Stickers + transitions — same compositor the 3D export path uses.
    // Caption is excluded here; `renderStatefulResultCard` draws it in its own pass.
    this._lastExportOverlayStickerCount = overlay.stickers.length;
    composeCreativeOverlay(ctx, overlay, width, height, { drawCaption: false });

    return { canvas, drawn };
  }

  async exportReel(request: ExportRequest & { tick?: number }): Promise<ExportResult> {
    const { sourcePack, resolved, width, height } = request;

    // MF1: build an OWN offscreen video for the request's pack. No dependency on
    // a live editor session (this.session) — capture works with no prior
    // createPreview and always renders the requested pack (pack-keyed).
    const variant = selectRenderVariant(sourcePack, "hybrid-video");
    const built = this.buildOffscreenCaptureVideo(sourcePack, variant);
    if (!built) {
      return { ok: false, reason: "encode-failed", fallbackSuggested: true };
    }
    const { video, host } = built;

    try {
      // Ensure the offscreen element has decoded a frame before seeking/drawing.
      const loaded = await this.waitForCaptureVideoReady(video);
      if (!loaded) {
        return { ok: false, reason: "encode-failed", fallbackSuggested: true };
      }

      // Build the binding-based hybrid timeline (H1) and compute the export edit tick.
      const timeline = buildHybridTimeline(sourcePack, resolved, variant);
      const maxEdit = timeline.durationTicks <= 0 ? 0 : timeline.durationTicks - 1;
      const editTick =
        request.tick !== undefined
          ? Math.max(0, Math.min(Math.round(request.tick), maxEdit))
          : timeline.durationTicks <= 0
            ? 0
            : Math.min(45, maxEdit);

      // Seek the muted offscreen video to the H1-mapped media time (H4 gate).
      const videoTimeSec = hybridEditTickToVideoTimeSec(
        timeline,
        editTick,
        sourcePack.tickRate,
      );
      // MF4: pass the variant's seekDeadbandSec and gate the result — if the
      // seek does not commit (timed-out / out-of-tolerance) after the built-in
      // retries, return encode-failed rather than drawing a stale frame.
      const seekDeadbandSec =
        typeof variant.config?.seekDeadbandSec === "number"
          ? variant.config.seekDeadbandSec
          : 0.08;
      const seekResult = await seekVideoWithGate(video, videoTimeSec, {
        deadbandSec: seekDeadbandSec,
      });
      if (seekResult.status !== "committed") {
        return { ok: false, reason: "encode-failed", fallbackSuggested: true };
      }

      // Verify the frame can actually be composited before committing to the card.
      // A throw here (e.g. HAVE_NOTHING state, cross-origin block) means no usable frame.
      let frameVerified = false;
      try {
        const probe = document.createElement("canvas");
        probe.width = 1;
        probe.height = 1;
        const probeCtx = probe.getContext("2d");
        if (probeCtx) {
          probeCtx.drawImage(video, 0, 0, 1, 1);
          frameVerified = true;
        }
      } catch {
        frameVerified = false;
      }
      if (!frameVerified) {
        return { ok: false, reason: "encode-failed", fallbackSuggested: true };
      }

      // Resolve the active frame crop for the beat at this edit tick.
      const activeSeg =
        timeline.segments.find(
          (s) => editTick >= s.editStartTick && editTick < s.editEndTick,
        ) ??
        timeline.segments[timeline.segments.length - 1] ??
        null;
      const frameOp = activeSeg ? (resolved.frames.get(activeSeg.beatId) ?? null) : null;

      // Resolve caption at the corresponding source tick.
      const sourceTick = Math.round(videoTimeSec * sourcePack.tickRate);
      const captionState = resolvePreviewCaption(sourcePack, resolved.captions, sourceTick);

      const watermark = sourcePack.exportSafety.requiredWatermark;

      // ── MF5: look grade + vignette + stickers/transitions ──────────────────
      // Resolve the look grade and creative overlay (stickers, transitions) at
      // this export frame. These are applied to the canvas so the exported card
      // matches what the player saw in the live preview.
      const lookState = sampleLook(sourcePack, resolved);
      const creative = sampleCreativeRenderState({
        pack: sourcePack,
        resolved,
        editTick,
        sourceTick,
        seed: request.editGraph?.seed ?? 0,
      });

      // Wait for the curated sticker art to be decoded before compositing —
      // `composeCreativeOverlay` is synchronous per-frame, so the images must
      // already be ready or the export falls back to procedural shapes.
      // Idempotent (cache-checked) so this costs nothing after the module-init
      // warm-up above has already settled.
      await preloadAllStickerArt();

      // Pre-composite: video frame + look grade/vignette + stickers/transitions.
      // Pass the frame geometry so the crop/zoom is baked into the canvas.
      const vw = video.videoWidth || width;
      const vh = video.videoHeight || height;
      const geom = computeFrameDrawGeometry(frameOp, vw, vh, width, height);
      const { canvas: compositeCanvas, drawn: frameDrawn } = this.buildExportCanvas(
        video,
        geom,
        lookState,
        creative.overlay,
        width,
        height,
      );

      // If the real video draw failed (e.g. CORS block after probe passed), do NOT
      // return a background-only card as ok:true — report encode-failed.
      if (!frameDrawn) {
        return { ok: false, reason: "encode-failed", fallbackSuggested: true };
      }

      // Pass the pre-composited canvas (crop + look + stickers already baked in) to
      // renderStatefulResultCard for caption/score/watermark chrome. frame is null
      // because the geometry has already been applied in buildExportCanvas.
      // MF5-final: supply reportImageDrawn so a silent composite-canvas drawImage
      // failure (e.g. tainted canvas, CORS race after the probe passed) surfaces
      // as encode-failed rather than an ok:true background-only card.
      let blob: Blob;
      let finalImageDrawn = true; // callback flips to false on a silent draw failure
      try {
        blob = await renderStatefulResultCard({
          width,
          height,
          image: compositeCanvas,
          videoWidth: width,
          videoHeight: height,
          frame: null,
          caption: captionState.fullText,
          captionStyle: captionState.style,
          captionScaleMilli: captionState.scaleMilli,
          captionAnchorXMilli: captionState.anchorXMilli,
          captionAnchorYMilli: captionState.anchorYMilli,
          scoreText: request.resultCardScoreText ?? "",
          watermark,
          showReconstructionLabel: shouldShowReconstructionLabel(sourcePack, resolved),
          theme: request.resultCardTheme ?? "default",
          reportImageDrawn: (drawn) => { finalImageDrawn = drawn; },
        });
      } catch {
        return { ok: false, reason: "encode-failed", fallbackSuggested: true };
      }
      if (!finalImageDrawn) {
        return { ok: false, reason: "encode-failed", fallbackSuggested: true };
      }

      return {
        ok: true,
        mimeType: "image/png",
        data: blob,
        attribution: sourcePack.license.attributions.concat([
          `SourcePack ${sourcePack.id}@${sourcePack.version}`,
          `Variant ${this.id}`,
          "Export is a watermarked PNG result card (muted hybrid video frame).",
        ]),
        watermark,
      };
    } finally {
      // Always dispose the offscreen element — never perturbs the live preview.
      video.remove();
      host.remove();
    }
  }
}
